import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { applyTheme, getTheme } from "../../lib/theme";
import { LandingMotion } from "../marketing/LandingMotion";
import { MarketingFooter, MarketingHeader } from "../marketing/MarketingChrome";
import { TariiWord } from "../marketing/TariiWord";
import { useHeaderOffset } from "../marketing/useHeaderOffset";
import "../marketing/landing-palette.css";
import "../marketing/word-design.css";
import "../marketing/points/points.css";
import "./workflow-timeline.css";
import { HowItWorksStory } from "../marketing/calendar/HowItWorksStory";
import { BLOCKS, blockFromHash } from "../marketing/calendar/storyScenes";

/**
 * `/how-it-works` tells one story on one stage (#2625): a month of the team
 * in five named chapters, from setting up to the hours at the end. The stage
 * stays pinned while the story's parts scroll up beside it, one by one. The
 * hash (`#set-up` to `#end-of-month`) follows the chapter in view, and the
 * old `#point-1` to `#point-10` links open the chapter that holds the point.
 */
export function HowItWorksPage() {
  const { hash } = useLocation();
  const navigate = useNavigate();
  useHeaderOffset();
  // The chapter the page opened on. Later chapters come from the scroll.
  const [startChapter] = useState(() => blockFromHash(hash));
  const go = useCallback(
    (index: number) => {
      const next = BLOCKS[Math.max(0, Math.min(BLOCKS.length - 1, index))];
      if (next) navigate({ hash: next.id }, { replace: true });
    },
    [navigate],
  );
  // With a mouse, the header slides away so the stage fills the screen, and
  // comes back while the pointer is near the top (#2809). Touch screens keep it.
  const [headerOpen, setHeaderOpen] = useState(false);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
    // The header height, read once and again on a resize, not on every move.
    let h = 64;
    const measure = () => {
      // --la-header-h is in the page's own px; clientY is in screen px, so
      // scale it by the laptop zoom (#2817).
      const css = getComputedStyle(document.documentElement);
      const z = Number.parseFloat(css.getPropertyValue("--la-site-z")) || 1;
      h = (Number.parseFloat(css.getPropertyValue("--la-header-h")) || 64) * z;
    };
    measure();
    window.addEventListener("resize", measure);
    const move = (e: PointerEvent) => {
      if (e.clientY <= 24) setHeaderOpen(true);
      else if (e.clientY > h + 24) setHeaderOpen(false);
    };
    window.addEventListener("pointermove", move, { passive: true });
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("resize", measure);
    };
  }, []);
  useLayoutEffect(() => {
    applyTheme("sage-clinic");
    return () => applyTheme(getTheme());
  }, []);
  // A cold load with a hash (`#sick-day`, or an old `#point-7`) scrolls that
  // chapter's title to the middle of the screen once, so its first beat is
  // on stage. Later chapters change the hash only, so the page stays put.
  // `ScrollToTopOnRouteChange` skips a location with a hash.
  // biome-ignore lint/correctness/useExhaustiveDependencies: first load only.
  useEffect(() => {
    if (!hash) return;
    const id = BLOCKS[startChapter]?.id;
    document.getElementById(`${id}-title`)?.scrollIntoView({ block: "center" });
  }, []);
  return (
    <LandingMotion>
      <div
        data-la-root=""
        data-header-open={headerOpen ? "" : undefined}
        className="wt-page wt-page-story min-h-screen bg-bg"
      >
        <div className="la-pinned-header">
          <div className="la-pinned-header-inner">
            <MarketingHeader />
          </div>
        </div>
        <main className="relative z-10">
          <HowItWorksStory
            startChapter={startChapter}
            onChapter={go}
            title={
              <>
                How <TariiWord /> works
              </>
            }
          />
          <div className="wt-main wt-main-rail wt-main-after">
            <div className="wt-trial">
              <p>
                Try <TariiWord /> with your team.
              </p>
              <Link to="/pricing">Arrange a free trial</Link>
            </div>
            <MarketingFooter />
          </div>
        </main>
      </div>
    </LandingMotion>
  );
}
